// Lineage traversal — FXC-8538 (Warp).
//
// The antecedent/descendant primitive. Mirrors Thread's server-side
// lineage queries (`/nodes/lineage`, `/nodes/dependencies` in
// thread-api): walk `parent → child` edges backward for antecedents
// (upstream) and forward for descendants (downstream), depth-bounded.
//
// Built around an adjacency index so a backing graph with millions of
// atomic nodes (every output scalar — CL, CD, mesh, geometry — is a
// node) can be traversed cheaply, and a surface renders only the
// revealed neighbourhood rather than the whole hairball.

import type { Graph, GraphEdge, GraphNode } from "./types";

/**
 * Pre-built adjacency index over a graph's edges. Canonical edges run
 * `source` (parent) → `target` (child), so:
 *   - antecedents (upstream)  = follow `predecessors` (target → sources)
 *   - descendants (downstream) = follow `successors`   (source → targets)
 */
export interface GraphIndex {
  nodeById: Map<string, GraphNode>;
  /** target id → ids of its parents (antecedent step). */
  predecessors: Map<string, string[]>;
  /** source id → ids of its children (descendant step). */
  successors: Map<string, string[]>;
  /** source id → canonical outgoing edges. */
  edgesBySource: Map<string, GraphEdge[]>;
}

/** Build an adjacency index. O(V + E); reuse it across many traversals. */
export function buildIndex(graph: Graph): GraphIndex {
  const nodeById = new Map<string, GraphNode>();
  for (const n of graph.nodes) nodeById.set(n.id, n);

  // Dedupe neighbours via Sets — parallel edges (same source→target more
  // than once) must not list a neighbour twice; that would double-count in
  // traversal frontiers and in directAncestors/directDescendants.
  // edgesBySource keeps every edge (parallel edges are real edges, needed
  // for subgraph reconstruction).
  const predSet = new Map<string, Set<string>>();
  const succSet = new Map<string, Set<string>>();
  const edgesBySource = new Map<string, GraphEdge[]>();
  for (const e of graph.edges) {
    (succSet.get(e.source) ?? succSet.set(e.source, new Set()).get(e.source)!).add(e.target);
    (predSet.get(e.target) ?? predSet.set(e.target, new Set()).get(e.target)!).add(e.source);
    (edgesBySource.get(e.source) ?? edgesBySource.set(e.source, []).get(e.source)!).push(e);
  }
  const predecessors = new Map<string, string[]>();
  for (const [k, v] of predSet) predecessors.set(k, [...v]);
  const successors = new Map<string, string[]>();
  for (const [k, v] of succSet) successors.set(k, [...v]);
  return { nodeById, predecessors, successors, edgesBySource };
}

export type LineageDirection = "ancestors" | "descendants" | "both";

export interface LineageOptions {
  /** Max hops to walk. Default 10 (matches thread-api's default). */
  depth?: number;
  /** Which way to walk. Default "both". */
  direction?: LineageDirection;
  /** Include the seed node id in the result. Default true. */
  includeSeed?: boolean;
}

/**
 * Reachable node ids from `seed`, with the number of hops to each. BFS,
 * so `depthOf` is the shortest hop count. The seed is depth 0.
 */
export interface LineageResult {
  ids: Set<string>;
  depthOf: Map<string, number>;
}

/** Antecedents (upstream / what this node depends on). */
export function ancestors(index: GraphIndex, seed: string, depth = 10): LineageResult {
  return walk(index.predecessors, seed, depth, true);
}

/** Descendants (downstream / what depends on this node). */
export function descendants(index: GraphIndex, seed: string, depth = 10): LineageResult {
  return walk(index.successors, seed, depth, true);
}

/**
 * Neighbourhood around a node — antecedents and/or descendants to a
 * depth. This is what a focus+expand explorer reveals on click.
 */
export function neighborhood(
  index: GraphIndex,
  seed: string,
  opts: LineageOptions = {},
): LineageResult {
  const { depth = 10, direction = "both", includeSeed = true } = opts;
  const ids = new Set<string>();
  const depthOf = new Map<string, number>();
  if (includeSeed) {
    ids.add(seed);
    depthOf.set(seed, 0);
  }
  if (direction === "ancestors" || direction === "both") {
    merge(ids, depthOf, walk(index.predecessors, seed, depth, includeSeed));
  }
  if (direction === "descendants" || direction === "both") {
    merge(ids, depthOf, walk(index.successors, seed, depth, includeSeed));
  }
  return { ids, depthOf };
}

/** Direct (1-hop) antecedents of a node, as canonical nodes. */
export function directAncestors(index: GraphIndex, id: string): GraphNode[] {
  return (index.predecessors.get(id) ?? []).map((p) => index.nodeById.get(p)).filter(isNode);
}

/** Direct (1-hop) descendants of a node, as canonical nodes. */
export function directDescendants(index: GraphIndex, id: string): GraphNode[] {
  return (index.successors.get(id) ?? []).map((c) => index.nodeById.get(c)).filter(isNode);
}

/**
 * The induced subgraph over a set of ids — the nodes and the edges whose
 * endpoints are both present. Pass a `GraphIndex` on hot paths so slicing
 * scales with the revealed ids instead of the full backing graph.
 */
export function subgraph(graph: Graph, ids: Set<string>): Graph;
export function subgraph(index: GraphIndex, ids: Set<string>): Graph;
export function subgraph(source: Graph | GraphIndex, ids: Set<string>): Graph {
  const nodes: GraphNode[] =
    "nodeById" in source
      ? [...ids].map((id) => source.nodeById.get(id)).filter(isNode)
      : source.nodes.filter((n) => ids.has(n.id));
  const nodeIds = new Set(nodes.map((n) => n.id));
  const edges: GraphEdge[] =
    "edgesBySource" in source
      ? [...nodeIds].flatMap((id) =>
          (source.edgesBySource.get(id) ?? []).filter((e) => nodeIds.has(e.target)),
        )
      : source.edges.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target));
  const nodeTypes = distinct(nodes.map((n) => n.type));
  const edgePredicates = distinct(edges.map((e) => e.predicate));
  return { nodes, edges, nodeTypes, edgePredicates };
}

// ── internals ──────────────────────────────────────────────────────────

function walk(
  adj: Map<string, string[]>,
  seed: string,
  depth: number,
  includeSeed: boolean,
): LineageResult {
  const ids = new Set<string>();
  const depthOf = new Map<string, number>();
  if (includeSeed) {
    ids.add(seed);
    depthOf.set(seed, 0);
  }
  let frontier = [seed];
  for (let d = 1; d <= depth && frontier.length > 0; d++) {
    const next: string[] = [];
    for (const id of frontier) {
      for (const nbr of adj.get(id) ?? []) {
        if (!depthOf.has(nbr)) {
          depthOf.set(nbr, d);
          ids.add(nbr);
          next.push(nbr);
        }
      }
    }
    frontier = next;
  }
  return { ids, depthOf };
}

function merge(ids: Set<string>, depthOf: Map<string, number>, from: LineageResult): void {
  for (const id of from.ids) {
    ids.add(id);
    const d = from.depthOf.get(id) ?? 0;
    const prev = depthOf.get(id);
    if (prev === undefined || d < prev) depthOf.set(id, d);
  }
}

function isNode(n: GraphNode | undefined): n is GraphNode {
  return n !== undefined;
}

function distinct(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    if (!seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}
